import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Plus, Minus } from "lucide-react";
import Sidebar from '../../sections/Sidebar';

interface PlaceOrderProps {
  userId: string;
}

interface MenuItem {
  id: string;
  name: string;
  price: number;
  image: string;
  description: string;
  category: "veg" | "non-veg";
}

interface SelectedItem {
  item: MenuItem;
  quantity: number;
}

const PlaceOrder: React.FC<PlaceOrderProps> = ({ userId }) => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const initialFormData = (state as { formData?: any; selectedItems?: SelectedItem[] })?.formData || {
    name: "",
    contact: "",
    email: "",
    eventDate: "",
    guestCount: "",
    deliveryMethod: "",
    specialRequests: "",
    address: "",
  };
  const initialSelectedItems = (state as { selectedItems?: SelectedItem[] })?.selectedItems || [];
  const [formData, setFormData] = useState(initialFormData);
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>(initialSelectedItems);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectItems = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    navigate("/services/party-order/menu", { state: { formData, selectedItems } });
  };

  const handleQuantityChange = (itemId: string, change: number) => {
    setSelectedItems((prevItems) => {
      const updatedItems = prevItems.map((si) => {
        if (si.item.id === itemId) {
          const newQuantity = Math.max(0, si.quantity + change);
          return { ...si, quantity: newQuantity };
        }
        return si;
      }).filter((si) => si.quantity > 0);
      return updatedItems;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast.error("Online ordering is temporarily disabled. We are still taking Party & Takeaway Orders via phone: 07521 262119");
  };

  return (
    <>
      <Sidebar />
      <section className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col items-center py-10 px-4 text-gray-900 dark:text-white">
        <button
          onClick={() => navigate(-1)}
          className="md:hidden flex items-center text-gray-900 dark:text-white mb-4 self-start"
        >
          <ArrowLeft className="w-6 h-6 mr-2" />
          Back
        </button>
        <h1 className="text-4xl font-bold mb-4 mt-8">
          Party Order
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 text-center">
          Order delicious food for your event!
        </p>

        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl p-6 w-full max-w-lg border border-gray-100 dark:border-gray-700">
          <h2 className="text-2xl font-semibold mb-6">
            Place Your Order
          </h2>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name
              </label>
              <input
                type="text"
                name="name"
                id="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="block w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-emerald-600 focus:border-emerald-600 outline-none transition-all"
              />
            </div>
            <div>
              <label htmlFor="contact" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Contact Number
              </label>
              <input
                type="tel"
                name="contact"
                id="contact"
                value={formData.contact}
                onChange={handleChange}
                required
                className="block w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-emerald-600 focus:border-emerald-600 outline-none transition-all"
              />
            </div>
            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Address
              </label>
              <textarea
                name="address"
                id="address"
                value={formData.address}
                onChange={handleChange}
                rows={3}
                required
                className="block w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-emerald-600 focus:border-emerald-600 outline-none transition-all"
                placeholder="Enter the event address"
              />
            </div>
            <div>
              <label htmlFor="eventDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Event Date
              </label>
              <input
                type="date"
                name="eventDate"
                id="eventDate"
                value={formData.eventDate}
                onChange={handleChange}
                required
                className="block w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-emerald-600 focus:border-emerald-600 outline-none transition-all"
              />
            </div>
            <div>
              <label htmlFor="guestCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Number of Guests
              </label>
              <input
                type="number"
                name="guestCount"
                id="guestCount"
                value={formData.guestCount}
                onChange={handleChange}
                min="1"
                required
                className="block w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-emerald-600 focus:border-emerald-600 outline-none transition-all"
              />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                Selected Dishes
              </h3>
              {selectedItems.length === 0 ? (
                <p className="text-gray-500 italic text-sm">No dishes selected yet.</p>
              ) : (
                <ul className="space-y-3">
                  {selectedItems.map(({ item, quantity }) => (
                    <li
                      key={item.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl"
                    >
                      <span className="font-medium">
                        {item.name}
                      </span>
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-2 bg-white dark:bg-gray-700 rounded-full px-2 py-1 shadow-sm">
                          <button
                            type="button"
                            onClick={() => handleQuantityChange(item.id, -1)}
                            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                            disabled={quantity === 0}
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-6 text-center font-bold text-sm">
                            {quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleQuantityChange(item.id, 1)}
                            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <span className="text-emerald-600 font-bold text-sm">
                          £{(item.price * quantity).toFixed(2)}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                onClick={handleSelectItems}
                className="mt-3 w-full px-4 py-2 text-white bg-blue-500 hover:bg-blue-600 rounded-lg font-semibold transition-colors"
              >
                {selectedItems.length === 0 ? "Select Menu Items" : "Modify Menu Items"}
              </button>
            </div>
            <div>
              <label htmlFor="deliveryMethod" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Delivery or Pickup
              </label>
              <select
                name="deliveryMethod"
                id="deliveryMethod"
                value={formData.deliveryMethod}
                onChange={handleChange}
                required
                className="block w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-emerald-600 focus:border-emerald-600 outline-none transition-all"
              >
                <option value="">Select</option>
                <option value="Delivery">Delivery</option>
                <option value="Pickup">Pickup</option>
              </select>
            </div>
            <div>
              <label htmlFor="specialRequests" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Special Requests (Optional)
              </label>
              <textarea
                name="specialRequests"
                id="specialRequests"
                value={formData.specialRequests}
                onChange={handleChange}
                rows={3}
                className="block w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-emerald-600 focus:border-emerald-600 outline-none transition-all"
                placeholder="E.g., dietary preferences or delivery instructions"
              />
            </div>
            
            <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 p-4 rounded-xl mt-8">
              <p className="text-emerald-800 dark:text-emerald-400 text-sm font-medium text-center">
                Online ordering is temporarily disabled due to relocation.
                <br /> We are still taking <span className="font-bold">Party</span> & <span className="font-bold">Takeaway Orders</span> via phone.
                <br /> Please call <a href="tel:07521262119" className="font-bold underline">07521 262119</a>.
              </p>
            </div>
            
            <button
              type="button"
              disabled
              className="w-full px-4 py-3 text-white bg-gray-400 rounded-xl font-bold cursor-not-allowed shadow-lg"
            >
              Order Disabled
            </button>
          </form>
        </div>
      </section>
    </>
  );
};

export default PlaceOrder;